<template>
  <div class="cx-combobox" :class="variant ? `cx-combobox--${variant}` : ''" v-click-outside="onClickOutside">
    <span
      v-if="variant === Variant.Autocomplete && label"
      class="cx-combobox__label"
      :class="labelStyle">
      {{ label }}
    </span>
    <div class="cx-combobox__chips">
      <div
        v-if="variant !== Variant.Autocomplete"
        class="cx-combobox__chips-container"
        :class="applyClass.container"
        ref="container"
        @click="showDropdown">
        <cx-chip
          v-for="(chip, index) in chips"
          :item="chip"
          :isIcon="isIcon"
          :isSubtitle="isSubtitle"
          :isTooltip="isTooltip"
          :key="index"
          :input="onToggle"
          :disabled="disabled"
          :loading="loading"/>
        <input
          v-if="isTextInput"
          class="cx-combobox__input"
          :class="applyClass.input"
          type="text"
          :placeholder="placeholderText"
          :style="applyStyle.input"
          :size="textInput.length || 1"
          v-model="textInput"
          v-focus="isDropdownVisible"
          :disabled="disabled || loading"
          @keydown="onInput"/>
        <cx-spinner v-if="loading" :size="15" :width="1"></cx-spinner>
      </div>
      <div
        v-if="variant === Variant.Autocomplete"
        class="cx-combobox__chips-container"
        :class="applyClass.container"
        ref="container"
        @click="showDropdown">
        <div class="cx-combobox__autocomplete-chips">
          <span
            v-for="(chip, index) in chips"
            :key="index"
            class="cx-combobox__chip"
            :class="{ 'cx-combobox__chip--singleselect': !multiselect}">
            {{ autocompleteChipText(index, chip) }}
          </span>
          <input
            v-if="showInput"
            class="cx-combobox__input"
            :class="applyClass.input"
            type="text"
            :size="textInput.length || 1"
            v-model="textInput"
            v-focus="isDropdownVisible"
            :disabled="disabled || loading"
            @keydown="onInput"/>
        </div>
        <div class="cx-combobox__icons">
          <div class="cx-combobox__reset-icon" v-if="chips.length > 0">
            <cx-icon icon="close" :size="14" @click="onReset"></cx-icon>
          </div>
          <div class="cx-combobox__append-icon">
            <cx-icon icon="brightness_1" :size="10"></cx-icon>
          </div>
        </div>
        <cx-spinner v-if="loading" :size="15" :width="1"></cx-spinner>
      </div>
      <div class="cx-combobox__reset" v-if="variant !== Variant.Autocomplete">
        <cx-reset-button
          :disabled="(!chipsSize && !textInput.length) || disabled || loading"
          height="42"
          :onReset="onReset"/>
      </div>
    </div>
    <cx-popper
      v-if="isDropdown && popper && !disabled && !loading"
      :reference="popper.reference"
      :options="popper.options"
      ref="popperRef">
      <cx-options
        v-if="isDropdownVisible"
        :items="options"
        :value="value"
        :label="label"
        :activeItem="activeOption"
        :input="onToggle"/>
    </cx-popper>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUpdated, ComputedRef } from 'vue';
import { doesStringIncludeString } from '@/utils/stringOperations';
import listNavHelper from '@/utils/listnavigationhelper';
import CxChip from './CxChip.vue';
import CxOptions from './CxOptions.vue';
import CxResetButton from '@/components/CxResetButton.vue';
import CxPopper from '@/components/CxPopper.vue';
import popperOptions from './CxOptions.popper';
import CxSpinner from '@/components/CxSpinner.vue';
import CxIcon from '@/components/CxIcon.vue';
import { gettext } from '@/translations/gettext.setup';

interface Item {
  id: string,
  name: string,
  subtitle?: string
}

enum Variant {
  Combobox = 'combobox',
  Autocomplete = 'autocomplete'
}

interface Props {
  items: Item[],
  value: string[],
  input: (value: string[]) => void,
  multiselect?: boolean,
  label?: String,
  isChips?: boolean,
  isDropdown?: boolean,
  isTextInput?: boolean,
  isSubtitle?: boolean,
  isIcon?: boolean,
  isTooltip?: boolean,
  disabled?: boolean,
  loading?: boolean,
  placeholder?: string,
  variant?: `${Variant}`
}

const props = withDefaults(defineProps<Props>(), {
  multiselect: true,
  variant: Variant.Combobox
});

const { $gettext } = gettext;

const textInput = ref('');
const isDropdownVisible = ref(false);
const activeOption = ref<Item | null>(null);
const popper = ref<any>(null);

// Template Refs
const container = ref<HTMLElement | null>(null);
const popperRef = ref<InstanceType<typeof CxPopper> | null>(null);

const placeholderText = computed(() => {
  if (!props.isTextInput) return '';
  if (props.placeholder) return props.placeholder;
  const prefix = $gettext('Add');
  return `${prefix} ${props.label}`;
});

const applyStyle = computed(() => {
  const labelLengthWidthMultiplier = 7;
  return {
    input: {
      width: textInput.value.length ? 'auto' : `${placeholderText.value.length * labelLengthWidthMultiplier}px`,
      maxWidth: textInput.value.length && container.value ? `${container.value.clientWidth}px` : 'auto'
    }
  };
});

const applyClass = computed(() => {
  return {
    container: {
      'cx-combobox__chips-container--interactive': props.isTextInput,
      'cx-combobox__chips-container--multiselect': props.multiselect,
      'cx-combobox__chips-container--higher': props.isSubtitle && props.variant !== Variant.Autocomplete,
      'cx-combobox__chips-container--loading': props.loading,
      'cx-combobox__chips-container--disabled': props.disabled,
    },
    input: {
      'cx-combobox__input--disabled': props.disabled,
      'cx-combobox__input--loading': props.loading
    }
  };
});

const options = computed(() => {
  return props.items.filter(i => doesStringIncludeString(i.name, textInput.value));
});

const isSelected = (item: Item) => {
  return props.value.includes(item.id);
};

const chips = computed(() => {
  if (props.isChips) return props.items.filter(i => isSelected(i));
  return [];
});

const showInput = computed(() => {
  const isEmptySingleSelect = !props.multiselect && !chips.value.length;
  return props.isTextInput && (isEmptySingleSelect || props.multiselect);
});

const autocompleteChipText = (index: number, chip: Item) => {
  return index === chips.value.length - 1 ? chip.name : `${chip.name},`;
};

const labelStyle = computed(() => {
  return chips.value.length > 0 || isDropdownVisible.value || textInput.value.length ? 'cx-combobox__label--outside' : '';
});

const hideDropdown = () => {
  isDropdownVisible.value = false;
  activeOption.value = null;
};

const chipsSize = computed(() => {
  const size = chips.value.length;
  if (!size) hideDropdown();
  return size;
});

/**
  * Updates the list of selected chips/dropdown options basing on the ongoing selection or deselection of a single chip/dropdown option.
*/
const onToggle = (item) => {
  let ids;
  if (props.multiselect) {
    ids = isSelected(item) ? props.value.filter(i => i !== item.id) : [...props.value, item.id];
  } else {
    ids = isSelected(item) ? props.value.filter(i => i !== item.id) : [item.id];
    textInput.value = '';
  }
  props.input(ids);
};

const onReset = () => {
  props.input([]);
  textInput.value = '';
  hideDropdown();
};

const showDropdown = () => {
  if (props.isDropdown && !isDropdownVisible.value) isDropdownVisible.value = true;
};

const onInput = (e) => {
  switch (e.key) {
    case 'Enter': {
      if (!isDropdownVisible.value) showDropdown();
      if (!activeOption.value && options.value.length) {
        const [firstOption] = options.value;
        onToggle({ id: firstOption.id, isChipSelected: !isSelected(firstOption) });
        activeOption.value = firstOption;
      } else {
        const item = options.value.find(i => activeOption.value && i.id === activeOption.value.id);
        if (item) {
          const { id } = item;
          onToggle({ id, isChipSelected: !isSelected(item) });
        }
      }
      break;
    }
    case 'Escape': {
      hideDropdown();
      break;
    }
    case 'Backspace': {
      if (chipsSize.value && (!textInput.value.length || !e.target.selectionStart)) {
        const item = chips.value[chipsSize.value - 1];
        onToggle({ id: item.id, isChipSelected: !isSelected(item) });
      }
      break;
    }
    case 'ArrowUp':
    case 'ArrowDown': {
      e.preventDefault();
      if (!isDropdownVisible.value) showDropdown();
      activeOption.value = listNavHelper.getActiveItem(options.value, activeOption.value, e.key);
      break;
    }
    default: {
      showDropdown();
      break;
    }
  }
};

const onClickOutside = () => {
  hideDropdown();
};

onMounted(() => {
  if (props.isDropdown) {
    popper.value = {
      reference: container.value,
      options: popperOptions
    };
  }
});

onUpdated(() => {
  if (props.isDropdown && !props.disabled && !props.loading && popperRef.value) popperRef.value.updatePopper();
});

// Directive
const VFocus = (el, binding) => {
  if (binding.value) el.focus();
};

</script>

<style lang="less">
@import '../../common';

.cx-combobox {
  pointer-events: none;
  padding: 5px 0;

  .cx-combobox__chips {
    pointer-events: auto;
    width: 100%;
    padding: 3px 0;
    .flex(row, flex-start, center);

    .cx-combobox__chips-container {
      position: relative;
      width: 100%;
      .flex(row, flex-start, center);
      flex-wrap: wrap;
      border: 1px solid @chipsBorderColor;
      border-radius: @inputBorderRadius;
      color: @textColorDisabled;
      user-select: none;
      margin: 0 3px 0 0;
      padding-bottom: 1px;
      cursor: auto;
      min-height: 42px;

      &--interactive {
        cursor: text;
      }

      &--disabled, &--loading {
        cursor: default;
      }

      &--higher {
        min-height: 49px;
      }

      .cx-combobox__input {
        padding: 10px 0 10px 10px;
        font-size: @appFontSize;
        outline-width: 0;
        color: @textColorBase;
        caret-color: @blueBtnColor;

        &::placeholder {
          font-size: @appFontSize;
          opacity: 0.6;
          color: @textColor;
        }

        &.cx-combobox__input--disabled {
          cursor:default;

           &::placeholder {
            opacity: 0.3;
           }
        }

        &.cx-combobox__input--loading {
          &::placeholder {
            opacity: 0;
          }
        }
      }

      .cx-spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    .cx-combobox__reset {
      height: @inputHeightHigher;
    }
  }

  &--autocomplete {
    width: 100%;
    position: relative;
    padding-top: 10px;

    .cx-combobox__label {
      position: absolute;
      top: 35%;
      left: 15px;
      color: @textColor;
      transform-origin: top font-size;

      &--outside {
        top: -3px;
        font-size: 9px;
      }
    }

    &:focus-within {
      .cx-combobox__label {
        top: -3px;
        font-size: 9px;
      }
    }

    .cx-combobox__chips {
      .cx-combobox__chips-container {
        background-color: @inputBackground;
        border: none;
        min-height: 32px;
        margin: 0;

        &:hover {
          background-color: @inputBackgroundHover;
          color: @textColorHighlight;
        }

        .cx-combobox__autocomplete-chips {
          overflow: hidden;
          max-width: 80%;
          padding: 8px 0px 8px 5px;
        }

        &--multiselect {
          display: flex;
          align-items: center;
          flex-wrap: nowrap;

          .cx-combobox__autocomplete-chips {
            display: flex;
            flex-wrap: wrap;
            max-height: 75px;
            max-width: 100%;
            overflow: auto;
          }
        }


        .cx-combobox__chip {
          color: @textColor;
          padding-left: 10px;
          font-size: 11px;

          &:last-of-type {
            padding-right: 10px;
          }

          &--singleselect {
            overflow: hidden;
            white-space: nowrap;
          }
        }

        .cx-combobox__icons {
          display: flex;
          align-items: center;
          margin-left: auto;
          padding-right: 10px;
          gap: 15px;
        }

        .cx-combobox__reset-icon {
          cursor: pointer;
          color: @iconColor;

          &:hover {
            color: @textColor;
          }
        }

        .cx-combobox__append-icon {
          color: @iconColor;
        }

        .cx-combobox__input {
          padding: 0;
          caret-color: #FFFFFF;
        }
      }
    }
  }

  .cx-popper {
    pointer-events: auto;
    z-index: @comboboxDropdownZI;
    background-color: @dropdownBackgroundColor;
    border-radius: @inputBorderRadius;

    .cx-options {
      max-height: @inputPadding + @cxOptionSize * 8 + @inputPadding;
      overflow-y: scroll;
    }
  }

  .cx-option {
    white-space: nowrap;
  }
}
</style>